"use client"

import { FC, useEffect, useRef, useState } from "react"
import { useContent } from "@/src/context/ContentContext"
import { StatItem } from "@/src/lib/content/types"

interface StatItemProps extends StatItem {
  animate: boolean
}

const StatCard: FC<StatItemProps> = ({ value, suffix, description, animate }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!animate) return
    let current = 0
    const steps = 60
    const increment = value / steps
    const intervalMs = 1500 / steps

    const timer = setInterval(() => {
      current += increment
      if (current >= value) {
        setCount(value)
        clearInterval(timer)
      } else {
        setCount(Math.floor(current))
      }
    }, intervalMs)

    return () => clearInterval(timer)
  }, [animate, value])

  return (
    <div className="flex flex-col items-center text-center group">
      <div
        className="text-5xl lg:text-6xl font-bold text-white leading-none mb-3 tabular-nums"
        aria-live="polite"
      >
        {count}
        <span className="text-3xl lg:text-4xl">{suffix}</span>
      </div>
      <p className="text-sm font-light text-gray-400">{description}</p>
      <div className="mt-4 w-8 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent group-hover:via-white transition-colors duration-500" />
    </div>
  )
}

const Stats: FC = () => {
  const { stats } = useContent()
  const sectionRef = useRef<HTMLElement>(null)
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="stats"
      className="py-16 lg:py-20 bg-gradient-to-b from-[#1e2126] via-[#111216] to-[#1e2126]"
      aria-label="Наші досягнення"
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {stats.map((stat) => (
            <StatCard key={stat.id} {...stat} animate={animate} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Stats
